import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import AdminLayout from "../components/AdminLayout";
import { AdminApi } from "../api/admin-api";
import { getEnv } from "../lib/utils";
import { toast } from "sonner";

export default function ManageInvites() {
  const [email, setEmail] = useState("");
  const [searchedEmail, setSearchedEmail] = useState("");
  const queryClient = useQueryClient();
  const studioUrl = getEnv("VITE_STUDIO_URL");

  const {
    data: inviteData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["adminInvites", searchedEmail],
    queryFn: () => AdminApi.getInvitesByEmail(searchedEmail),
    enabled: searchedEmail.length > 0,
    retry: false,
  });

  const resendMutation = useMutation({
    mutationFn: (inviteId: number) => AdminApi.resendInvite(inviteId),
    onSuccess: () => {
      toast.success("Invite email resent");
      queryClient.invalidateQueries({
        queryKey: ["adminInvites", searchedEmail],
      });
    },
    onError: (err) => {
      toast.error(`Failed to resend invite: ${err.message}`);
    },
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim()) {
      setSearchedEmail(email.trim());
    }
  };

  const copyInviteLink = (externalId: string) => {
    const link = `${studioUrl}/invite/${externalId}/accept`;
    navigator.clipboard.writeText(link);
    toast.success("Invite link copied to clipboard");
  };

  return (
    <AdminLayout
      title="Manage Invites"
      description="Search for invites by email and resend or copy invite links"
      breadcrumb="Manage Invites"
    >
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <form onSubmit={handleSearch} className="flex gap-3">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="user@example.com"
              required
            />
            <button
              type="submit"
              disabled={isLoading}
              className="px-6 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? "Searching..." : "Search"}
            </button>
          </form>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800 text-sm">
              {error instanceof Error
                ? error.message
                : "Failed to fetch invites"}
            </p>
          </div>
        )}

        {inviteData && (
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <p className="text-sm text-gray-600">
                Invites for:{" "}
                <span className="font-medium">{inviteData.email}</span>
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {inviteData.invites.length} invite
                {inviteData.invites.length !== 1 ? "s" : ""} found
              </p>
            </div>

            {inviteData.invites.length === 0 ? (
              <div className="px-6 py-8 text-center text-gray-500">
                No invites found for this email.
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {inviteData.invites.map((invite) => (
                  <div
                    key={invite.id}
                    className="px-6 py-4 flex items-center justify-between"
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-gray-900">
                          {invite.teamName || "(unnamed team)"}
                        </p>
                        <span
                          className={`text-xs font-medium px-2 py-0.5 rounded ${
                            invite.status === "accepted"
                              ? "bg-green-100 text-green-800"
                              : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {invite.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500">
                        Team ID: {invite.teamId} &middot;{" "}
                        {invite.teamExternalId}
                      </p>
                      {invite.organisationName && (
                        <p className="text-sm text-gray-500">
                          Org: {invite.organisationName} (ID:{" "}
                          {invite.organisationId})
                        </p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        Role: {invite.role} &middot; Created:{" "}
                        {new Date(invite.createdAt).toLocaleDateString()}
                        {invite.acceptedAt &&
                          ` · Accepted: ${new Date(invite.acceptedAt).toLocaleDateString()}`}
                      </p>
                    </div>
                    {invite.status === "pending" && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => resendMutation.mutate(invite.id)}
                          disabled={resendMutation.isPending}
                          className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Resend
                        </button>
                        <button
                          onClick={() => copyInviteLink(invite.externalId)}
                          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-md hover:bg-gray-100 transition-colors"
                        >
                          Copy link
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
